<template>
  <div class="app-container" :class="{ 'community-mobile': isCommunityMobile, 'mobile-nav-open': mobileNavOpen }">
    <AppSidebar
      v-if="showSidebar"
      :user="user"
      :identities="identities"
      :active-path="route.fullPath"
      :collapsed="isSidebarCollapsed && !isCommunityMobile"
      :is-admin="isAdmin"
      :is-enterprise-admin="isEnterpriseAdmin"
      :is-personal-user="isPersonalUser"
      :messages-enabled="messagesEnabled"
      :message-unread-count="messageCenter.state.unreadCount"
      :preference-complete="!profileCheckDone || profileCompleted"
      @navigate="handleNavigate"
      @user-command="handleUserCommand"
      @switch-identity="handleSwitchIdentity"
      @xbios-action="sendXbiosAction"
      @toggle-collapse="isSidebarCollapsed = !isSidebarCollapsed"
      @open-messages="messageCenter.open()"
      @open-release-updates="releaseAnnouncementHost?.openCurrent()"
      @dialog-busy="sidebarDialogBusy = $event"
    />
    <!-- 成果社区手机端：主导航改为左侧抽屉（原型 .bu-cm-nav-backdrop / .bu-cm-nav-close）。 -->
    <template v-if="showSidebar && isCommunityMobile && mobileNavOpen">
      <button type="button" class="mobile-nav-backdrop" tabindex="-1" aria-label="关闭主导航" @click="mobileNavOpen = false"></button>
      <button type="button" class="mobile-nav-close" aria-label="关闭主导航" @click="mobileNavOpen = false">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </template>
    <main :class="['app-content', { 'login-page-content': isLoginPage, 'no-sidebar': !showSidebar, 'home-page-content': isHomePage, 'onboarding-page-content': isOnboardingPage, 'analysis-create-page-content': isAnalysisCreatePage }]">
      <div
        v-if="cachedAnalysisId"
        v-show="isCachedAnalysisVisible"
        class="persistent-analysis-host"
      >
        <AnalysisRunPage
          :key="`persistent-analysis-${cachedAnalysisId}`"
          :analysis-id="cachedAnalysisId"
        />
      </div>
      <!-- KeepAlive moves inactive DOM out of the document, which reloads iframe
           contents. Keep the visited AI workspace connected and only hide it. -->
      <div
        v-if="cachedAiChatKey"
        class="persistent-ai-chat-host"
        :class="{ 'is-hidden': !isProjectWorkspace }"
        :inert="!isProjectWorkspace"
        :aria-hidden="!isProjectWorkspace"
      >
        <AiChatPage :key="cachedAiChatKey" :active="isProjectWorkspace" />
      </div>
      <router-view v-slot="{ Component }">
        <KeepAlive
          v-if="keepAliveEnabled"
          :include="persistentWorkspaceComponents"
          :max="4"
        >
          <component v-if="!isAnalysisRunPage && !isProjectWorkspace" :is="Component" :key="routeViewKey" />
        </KeepAlive>
      </router-view>
    </main>
    <!-- 「问问 AI」浮标只在标准流程页出现；项目工作区里已有项目对话框，连面板一起隐藏。只隐藏不卸载，回来时对话仍在。 -->
    <div v-if="isLoggedIn && !isLoginPage && !isOnboardingPage && !isPrivacyPage && !isTermsPage && !isAdminPage && !isShortGiftPage" class="zeus-assistant-slot">
      <ZeusAssistantHost :key="currentIdentityId" :identity-id="currentIdentityId" :context="assistantPage" :hidden="isProjectWorkspace" :show-launcher="isStandardWorkflowPage" />
    </div>
    <PublicDataTour v-if="isLoggedIn && !isShortGiftPage" />
    <ReleaseAnnouncementHost
      ref="releaseAnnouncementHost"
      :user-id="user?.id"
      :authenticated="hasSessionToken && isLoggedIn"
      :onboarding-resolved="profileCheckDone"
      :business-page="showSidebar"
      :route-key="route.fullPath"
      :blocking="sidebarDialogBusy || messageCenter.state.open || Boolean(messageCenter.state.detailId)"
    />
    <MaintenanceNoticeHost
      :authenticated="hasSessionToken && isLoggedIn"
      :identity-id="currentIdentityId"
      :route-key="route.fullPath"
      :blocking="sidebarDialogBusy || messageCenter.state.open || Boolean(messageCenter.state.detailId)"
      :user-role="user?.role"
    />
    <MessageNotificationHost v-if="messagesEnabled" :center="messageCenter" :identity-id="currentIdentityId" />
  </div>
</template>

<script setup>
import { ref, computed, provide, defineAsyncComponent, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import ZeusAssistantHost from '@/components/ZeusAssistantHost.vue'
import { provideAssistantPage } from '@/composables/useAssistantPage'
import AppSidebar from '@/components/AppSidebar.vue'
import ReleaseAnnouncementHost from '@/components/ReleaseAnnouncementHost.vue'
import MaintenanceNoticeHost from '@/components/MaintenanceNoticeHost.vue'
import MessageNotificationHost from '@/components/MessageNotificationHost.vue'
import { clearMessageNavigations } from '@/utils/messageNavigationContext'
import { messageApi } from '@/api/messages'
import { useMessageNotifications } from '@/composables/useMessageNotifications'
import PublicDataTour from '@/components/PublicDataTour.vue'
import { getMyIdentities } from '@/api/credit'
import { selectIdentity, logout } from '@/api/auth'
import { checkResearchProfileStatus } from '@/api/profile'
import { connectOpenCode } from '@/utils/opencode'
import { track, Events } from '@/utils/tracker'
import { clearTcmClinicalSessionStorage } from '@/utils/tcmClinicalHandoff'
import { isShortGiftPath } from '@/router/giftHistory'
import { prewarmFinishedAnalysisMessages } from '@/utils/resultNotebookPrewarm'

const AiChatPage = defineAsyncComponent(() => import('@/views/aiChatPage.vue'))
const AnalysisRunPage = defineAsyncComponent(() => import('@/views/inspiration/AnalysisRunPage.vue'))

const router = useRouter()
const route = useRoute()
const assistantPage = provideAssistantPage(route)

const user = ref(null)
const identities = ref([])
const hasSessionToken = ref(false)
const releaseAnnouncementHost = ref(null)
const sidebarDialogBusy = ref(false)

const profileCheckDone = ref(false)
const profileCompleted = ref(false)
const keepAliveEnabled = ref(true)
const opencodeCacheVersion = ref(0)
const cachedAnalysisId = ref('')
const lastInspirationRoute = ref('/inspiration')
const isSidebarCollapsed = ref(false)
// 创建页携带一次性 analysisId。把它放进 KeepAlive 会在创建成功后保留已消费
// 的 ID，用户返回同一模板时再次 INSERT analysis_case 主键。列表和工作台可以
// 持久化，创建页必须每次重新挂载，生成一份新的创建意图。
const persistentWorkspaceComponents = ['InspirationPage']
const LAST_INSPIRATION_ROUTE_STORAGE_PREFIX = 'gutdb-last-inspiration-route'

let storageSyncTimer = null
let lastStoredUserJson = null
let identitiesInflight = null
let lastIdentitiesFetchedAt = 0
let profileCheckInflight = null
// checkLoginStatus 由 route.path 变化(无节流)和 storage 事件(仅 300ms 防抖)共同触发,
// identitiesInflight 只去重"正在进行中"的并发请求,连续触发时前一次完成后仍会立即打下一次。
// 这里加最小间隔节流,避免短时间内被反复调用时把 /api/credits/identities 打成请求风暴。
const MIN_IDENTITIES_FETCH_INTERVAL_MS = 3000

const isLoggedIn = computed(() => !!user.value)
const isLoginPage = computed(() => route.name === 'Login')
const isHomePage = computed(() => route.name === 'Home')
const isOnboardingPage = computed(() => route.name === 'Onboarding')
const isPrivacyPage = computed(() => route.name === 'Privacy')
const isTermsPage = computed(() => route.name === 'Terms')
const isShortGiftPage = computed(() => route.name === 'SubscriptionGiftShortClaim' || (!route.name && isShortGiftPath(window.location.pathname)))
const isAnalysisRunPage = computed(() => route.name === 'AnalysisRun')
// 「问问 AI」浮标只在能力空间与标准流程（创建 / 运行）页出现，避免与项目对话等入口混淆。
const STANDARD_WORKFLOW_ROUTES = new Set(['CapabilitySpace', 'AnalysisCreate', 'AnalysisRun', 'TcmClinicalCreate', 'TcmClinicalRun', 'TcmMicrobiotaCreate', 'TcmMicrobiotaDraft'])
const isStandardWorkflowPage = computed(() => STANDARD_WORKFLOW_ROUTES.has(route.name))
const isProjectWorkspace = computed(() => route.name === 'AiChat')
const isAnalysisCreatePage = computed(() => route.name === 'AnalysisCreate')
const currentRouteAnalysisId = computed(() => (
  typeof route.params.analysisId === 'string' ? route.params.analysisId : ''
))
const isCachedAnalysisVisible = computed(() => (
  isAnalysisRunPage.value &&
  Boolean(cachedAnalysisId.value) &&
  cachedAnalysisId.value === currentRouteAnalysisId.value
))
const isAdminPage = computed(() => route.path.startsWith('/admin'))
const isEnterprisePage = computed(() => route.path.startsWith('/enterprise'))
const isAdmin = computed(() => user.value?.role === 'admin')
const isEnterpriseAdmin = computed(() => user.value?.role === 'enterprise_admin')
const isPersonalUser = computed(() => user.value?.identityType === 1)
const currentIdentityId = computed(() => user.value?.identityId)
const messagesEnabled = ref(false)
let messageConfigGeneration = 0
watch(isLoggedIn, async loggedIn => {
  const generation = ++messageConfigGeneration
  messagesEnabled.value = false
  if (!loggedIn) return
  try {
    const config = await messageApi.config()
    if (generation === messageConfigGeneration) messagesEnabled.value = config.enabled === true
  } catch { /* 旧版或未开启时保留既有公告入口，不启动消息轮询。 */ }
}, { immediate: true })
const messageScope = computed(() => messagesEnabled.value && isLoggedIn.value && user.value?.identityId
  ? `${user.value.id || user.value.userId}:${user.value.identityId}` : '')
const messageCenter = useMessageNotifications(messageScope)
// 新消息到达时若有「标准分析已完成」，提前预热那次运行的结果页（切换身份归零时不触发）。
watch(() => messageCenter.state.throughMessageId, (next, previous) => {
  if (!previous || !(next > previous)) return
  messageApi.list({ pageSize: 5 })
    .then(result => prewarmFinishedAnalysisMessages(result?.items, previous))
    .catch(() => {})
})
provide('biointelos-messages-enabled', messagesEnabled)
provide('biointelos-message-return', (requestId, identityId) => {
  if (!messagesEnabled.value || currentIdentityId.value == null
    || (identityId != null && String(identityId) !== String(currentIdentityId.value))
    || typeof requestId !== 'string' || !/^[A-Za-z0-9-]{1,80}$/.test(requestId)) return false
  const event = new CustomEvent('biointelos-message-return', {
    cancelable: true,
    detail: { requestId, identityId: currentIdentityId.value },
  })
  window.dispatchEvent(event)
  return event.defaultPrevented
})
provide('biointelos-message-open-list', identityId => {
  if (!messagesEnabled.value || !isLoggedIn.value || !hasSessionToken.value
    || identityId == null || currentIdentityId.value == null
    || String(identityId) !== String(currentIdentityId.value)) return false
  void messageCenter.open()
  return true
})
const inspirationRouteStorageKey = identityId => (
  `${LAST_INSPIRATION_ROUTE_STORAGE_PREFIX}:${identityId || 'anonymous'}`
)
const safeInspirationRoute = value => (
  typeof value === 'string' && (
    value === '/inspiration' ||
    value.startsWith('/inspiration?') ||
    value.startsWith('/inspiration/')
  )
    ? value
    : '/inspiration'
)
const readLastInspirationRoute = identityId => {
  try {
    return safeInspirationRoute(sessionStorage.getItem(inspirationRouteStorageKey(identityId)))
  } catch {
    return '/inspiration'
  }
}
const rememberInspirationRoute = (fullPath, identityId = currentIdentityId.value) => {
  const rememberedRoute = safeInspirationRoute(fullPath)
  lastInspirationRoute.value = rememberedRoute
  try {
    sessionStorage.setItem(inspirationRouteStorageKey(identityId), rememberedRoute)
  } catch {
    // sessionStorage 不可用时仍保留当前应用生命周期内的内存状态。
  }
}
const routeViewKey = computed(() => {
  if (route.name === 'Billing') {
    return `billing-${currentIdentityId.value || 'anonymous'}`
  }
  return route.fullPath
})

// Cache only a visited workspace for this account and identity. A hidden
// workspace must be destroyed on scope/credential changes, without preloading
// the next identity until the user actually opens its AI page.
const cachedAiChatKey = ref('')
const aiChatScopeKey = computed(() => JSON.stringify([
  user.value?.id,
  currentIdentityId.value ?? user.value?.identityUsername,
  opencodeCacheVersion.value,
]))
watch(
  [isProjectWorkspace, isLoggedIn, hasSessionToken, keepAliveEnabled, aiChatScopeKey],
  ([visible, loggedIn, authenticated, enabled, scope]) => {
    if (!loggedIn || !authenticated || !enabled) cachedAiChatKey.value = ''
    else if (visible) cachedAiChatKey.value = scope
    else if (cachedAiChatKey.value !== scope) cachedAiChatKey.value = ''
  },
  { immediate: true, flush: 'sync' },
)

watch(
  [() => route.name, () => route.params.analysisId],
  ([name, analysisId]) => {
    if (name === 'AnalysisRun' && typeof analysisId === 'string' && analysisId) {
      cachedAnalysisId.value = analysisId
    }
  },
  { immediate: true, flush: 'sync' },
)

watch(
  () => route.fullPath,
  (fullPath) => {
    if (route.path.startsWith('/inspiration')) {
      rememberInspirationRoute(fullPath)
    }
  },
  { immediate: true },
)

watch(currentIdentityId, (identityId, previousIdentityId) => {
  if (previousIdentityId && identityId !== previousIdentityId) {
    clearMessageNavigations()
    cachedAnalysisId.value = ''
    clearTcmClinicalSessionStorage()
  }
  if (route.path.startsWith('/inspiration')) {
    rememberInspirationRoute(route.fullPath, identityId)
    if (isAnalysisRunPage.value && currentRouteAnalysisId.value) {
      cachedAnalysisId.value = currentRouteAnalysisId.value
    }
  } else {
    lastInspirationRoute.value = readLastInspirationRoute(identityId)
  }
})
const showSidebar = computed(() => {
  if (isShortGiftPage.value) return false
  if (isLoginPage.value) return false
  if (isHomePage.value) return false
  if (isOnboardingPage.value) return false
  if (isPrivacyPage.value) return false
  if (isTermsPage.value) return false
  if (isAdminPage.value) return false
  if (isEnterprisePage.value) return false
  return true
})

// 仅成果社区列表页做手机端适配（≤760px，与原型 @media 一致）；其他页面保持桌面布局。
const MOBILE_COMMUNITY_QUERY = '(max-width: 760px)'
const mobileViewport = ref(false)
const mobileNavOpen = ref(false)
let mobileMedia = null
const syncMobileViewport = () => { mobileViewport.value = Boolean(mobileMedia?.matches) }
const isCommunityMobile = computed(() => mobileViewport.value && route.name === 'ResearchCommunity')
const openMobileNav = () => { if (isCommunityMobile.value) mobileNavOpen.value = true }
const closeMobileNavOnEscape = event => { if (event.key === 'Escape') mobileNavOpen.value = false }
watch([() => route.fullPath, isCommunityMobile], () => { mobileNavOpen.value = false })

const checkProfileStatus = async () => {
  if (isShortGiftPage.value) return
  if (!isLoggedIn.value || profileCheckDone.value) return
  if (isLoginPage.value || isHomePage.value || isOnboardingPage.value) return
  const accountId = user.value?.id
  if (profileCheckInflight && profileCheckInflight.accountId === accountId) return
  const operation = { accountId }
  profileCheckInflight = operation

  try {
    const res = await checkResearchProfileStatus()
    if (!isLoggedIn.value || user.value?.id !== accountId || profileCheckInflight !== operation) return
    if (res.data?.code === 200) {
      const isCompleted = res.data.data?.isCompleted
      profileCheckDone.value = true
      profileCompleted.value = !!isCompleted
      // 2026-10-18：首次进入的用户直接进平台，不再强制跳转研究偏好问卷；需要时从侧栏「我的研究偏好」填写。
    }
  } catch (err) {
    console.error('检查画像状态失败:', err)
  } finally {
    if (profileCheckInflight === operation) profileCheckInflight = null
  }
}

// 研究偏好保存成功（api/profile 只在 code 200 时广播）：侧栏「我的研究偏好」立即取消蓝色引导高亮；
// 丢弃保存前发出的状态查询，避免旧的「未完成」回包把高亮又改回来。
const onResearchPreferencesSaved = () => {
  profileCheckInflight = null
  profileCheckDone.value = true
  profileCompleted.value = true
}

watch(() => user.value?.id, accountId => {
  profileCheckDone.value = false
  profileCompleted.value = false
  profileCheckInflight = null
  if (accountId != null) void nextTick(checkProfileStatus)
}, { flush: 'sync' })

// 引导页保存或跳过后回到应用，重新收敛状态，避免沿用跳转前的“未完成”。
watch(isOnboardingPage, (isOnboarding, wasOnboarding) => {
  if (!wasOnboarding || isOnboarding) return
  profileCheckDone.value = false
  profileCompleted.value = false
  profileCheckInflight = null
  void checkProfileStatus()
})

const checkLoginStatus = () => {
  const storedUser = localStorage.getItem('user')
  const token = localStorage.getItem('token')
  const wasLoggedIn = !!user.value
  hasSessionToken.value = !!token

  if (storedUser) {
    if (storedUser !== lastStoredUserJson) {
      lastStoredUserJson = storedUser
      try {
        const parsed = JSON.parse(storedUser)
        if (parsed && typeof parsed === 'object') {
          user.value = parsed
        }
      } catch {
        if (!token) user.value = null
      }
    }
  } else {
    lastStoredUserJson = null
    if (!token) user.value = null
  }

  if (!wasLoggedIn && !!user.value) {
    profileCheckDone.value = false
  }
  fetchIdentities()
}

const fetchIdentities = async () => {
  if (isShortGiftPage.value) return
  if (!isLoggedIn.value || !localStorage.getItem('token')) {
    identities.value = []
    return
  }
  if (identitiesInflight) return identitiesInflight
  if (Date.now() - lastIdentitiesFetchedAt < MIN_IDENTITIES_FETCH_INTERVAL_MS) return

  identitiesInflight = (async () => {
    try {
      const ir = await getMyIdentities()
      if (ir.data?.code === 200) {
        identities.value = ir.data.data || []
      } else {
        identities.value = []
      }
    } catch {
      identities.value = []
    } finally {
      lastIdentitiesFetchedAt = Date.now()
      identitiesInflight = null
    }
  })()

  return identitiesInflight
}

const handleSwitchIdentity = async (identity) => {
  if (identity.id === currentIdentityId.value) return
  const fromType = user.value?.identityType
  try {
    const res = await selectIdentity(identity.id)
    if (res.data?.code === 200) {
      const data = res.data.data
      clearTcmClinicalSessionStorage()
      clearMessageNavigations()
      localStorage.setItem('token', data.token)
      localStorage.setItem('refreshToken', data.refreshToken)
      const userToStore = {
        ...data.user,
        identityId: data.identityId,
        identityType: data.identityType,
        role: data.role,
        enterpriseId: data.enterpriseId
      }
      localStorage.setItem('user', JSON.stringify(userToStore))
      try {
        track(Events.IDENTITY_SWITCH, { from_type: fromType, to_type: data.identityType })
      } catch (e) { /* ignore */ }
      ElMessage.success('身份切换成功')
      window.location.reload()
    } else {
      ElMessage.error(res.data?.message || '切换失败')
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.message || '切换失败')
  }
}

const openInNewTab = (path) => {
  const base = window.location.origin + window.location.pathname
  window.open(`${base}#${path}`, '_blank')
}

const handleNavigate = (path) => {
  if (path === '/' || path === '/ai-chat') {
    // 「我的项目」停在项目欢迎页（iframe 未进入具体项目）时主导航保持展开（原型）。
    if (path === '/ai-chat' && !xbiosProjectOpen.value) isSidebarCollapsed.value = false
    // 原型副本3「开始研究」：已在项目里再点一次回项目欢迎页；从其他模块点进来仍回到上次的项目/对话。
    if (path === '/ai-chat' && route.path === '/ai-chat' && xbiosProjectOpen.value) {
      sendXbiosAction('show_home')
      return
    }
    router.push(path)
    return
  }
  if (path === '/inspiration') {
    router.push(lastInspirationRoute.value)
    return
  }
  if (path.startsWith('/inspiration?') || path.startsWith('/inspiration/') || path === '/capabilities' || path.startsWith('/capabilities/')) {
    router.push(path)
    return
  }
  if (path === '/dataset' || path === '/community' || path === '/billing') {
    // 测试报告 P1：不再在进入数聚空间时强制折叠侧边栏（其他页面都保持用户当前的展开状态，离开后也不会恢复）。
    router.push(path === '/billing'
      ? { path, query: { returnTo: route.fullPath } }
      : path)
    return
  }
  openInNewTab(path)
}

const sendXbiosAction = (action) => {
  window.dispatchEvent(new CustomEvent('gutdb-xbios-action', {
    detail: { action }
  }))
}

let logoutPending = false
const handleUserCommand = async (command) => {
  if (command === 'logout') {
    if (logoutPending) return
    logoutPending = true
    const token = localStorage.getItem('token')
    const refreshToken = localStorage.getItem('refreshToken')
    const storedUser = localStorage.getItem('user')
    let serverConfirmed = false
    try {
      if (token) {
        const response = await logout(token)
        serverConfirmed = (response?.status === 200 && response?.data?.code === 200)
          || response?.status === 401 || response?.data?.code === 401
      }
    } catch (error) {
      serverConfirmed = error?.response?.status === 401 || error?.response?.data?.code === 401
    } finally {
      logoutPending = false
    }
    // A late response belongs only to the session that initiated this logout.
    if (localStorage.getItem('user') !== storedUser || localStorage.getItem('refreshToken') !== refreshToken
      || (!refreshToken && localStorage.getItem('token') !== token)) return
    clearMessageNavigations()
    clearTcmClinicalSessionStorage()
    keepAliveEnabled.value = false
    opencodeCacheVersion.value += 1
    cachedAnalysisId.value = ''
    lastInspirationRoute.value = '/inspiration'
    try {
      sessionStorage.removeItem(inspirationRouteStorageKey(currentIdentityId.value))
    } catch {
      // 忽略受限浏览器环境中的 sessionStorage 清理失败。
    }
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    hasSessionToken.value = false
    user.value = null
    if (serverConfirmed) ElMessage.success('已成功退出登录')
    else ElMessage.warning('已退出本机登录，服务端注销未确认')
    router.push('/login')
    nextTick(() => {
      keepAliveEnabled.value = true
    })
  } else if (command === 'profile') {
    router.push('/profile')
  } else if (command === 'my-credits') {
    openInNewTab('/my-credits')
  } else if (command === 'billing') {
    openInNewTab('/billing')
  } else if (command === 'xbios-settings') {
    sendXbiosAction('open_settings')
  }
}

watch(() => route.path, () => {
  checkLoginStatus()
}, { immediate: true })

watch([isLoggedIn, () => route.name], () => {
  if (isLoggedIn.value && route.name !== 'Login' && route.name !== 'Home' && route.name !== 'Onboarding') {
    setTimeout(() => {
      checkProfileStatus()
    }, 500)
  }
}, { immediate: true })

// xBIOS iframe 当前是否在具体项目里；欢迎页时为 false。
const xbiosProjectOpen = ref(false)

const onGutdbProjectSelected = () => {
  // iframe 内进入具体项目后自动折叠最左侧主导航栏（用户仍可通过 toggle 手动展开）
  xbiosProjectOpen.value = true
  isSidebarCollapsed.value = true
}

const onGutdbProjectCleared = () => {
  // iframe 回到项目欢迎页：主导航恢复展开（原型：欢迎页不自动折叠）
  xbiosProjectOpen.value = false
  if (route.name === 'AiChat') isSidebarCollapsed.value = false
}

const onStorageSync = () => {
  clearTimeout(storageSyncTimer)
  storageSyncTimer = setTimeout(() => {
    checkLoginStatus()
  }, 300)
}

const warmOpenCodeInstance = () => {
  const token = localStorage.getItem('token')
  if (!token) return
  connectOpenCode(token).catch(() => {})
}

const onLoginSuccess = () => {
  clearTcmClinicalSessionStorage()
  checkLoginStatus()
  profileCheckDone.value = false
  profileCompleted.value = false
  opencodeCacheVersion.value += 1
  warmOpenCodeInstance()
  setTimeout(() => {
    checkProfileStatus()
  }, 800)
}

onMounted(() => {
  checkLoginStatus()
  window.addEventListener('storage', onStorageSync)
  window.addEventListener('gutdb-login-success', onLoginSuccess)
  window.addEventListener('gutdb-project-selected', onGutdbProjectSelected)
  window.addEventListener('gutdb-project-cleared', onGutdbProjectCleared)
  window.addEventListener('gutdb-research-preferences-changed', onResearchPreferencesSaved)
  window.addEventListener('gutdb-mobile-nav-open', openMobileNav)
  window.addEventListener('keydown', closeMobileNavOnEscape)
  mobileMedia = window.matchMedia?.(MOBILE_COMMUNITY_QUERY) || null
  syncMobileViewport()
  mobileMedia?.addEventListener?.('change', syncMobileViewport)
  setTimeout(() => {
    checkProfileStatus()
  }, 1000)
})

onUnmounted(() => {
  clearTimeout(storageSyncTimer)
  window.removeEventListener('storage', onStorageSync)
  window.removeEventListener('gutdb-login-success', onLoginSuccess)
  window.removeEventListener('gutdb-project-selected', onGutdbProjectSelected)
  window.removeEventListener('gutdb-research-preferences-changed', onResearchPreferencesSaved)
  window.removeEventListener('gutdb-mobile-nav-open', openMobileNav)
  window.removeEventListener('keydown', closeMobileNavOnEscape)
  mobileMedia?.removeEventListener?.('change', syncMobileViewport)
  window.removeEventListener('gutdb-project-cleared', onGutdbProjectCleared)
})
</script>

<style scoped>
:global(html) {
  background-color: var(--bg-base);
}

.app-container {
  display: flex;
  font-family: var(--font-family-sans);
  background-color: var(--bg-base);
  min-height: 100vh;
}


.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 64px !important;
  padding: 0 32px;
  display: flex;
  align-items: center;
  background: var(--bg-header);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.header-logo {
  display: flex;
  align-items: center;
  margin-right: 40px;
  flex-shrink: 0;
}

.logo-img {
  height: 32px;
  width: auto;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
}

.nav-link {
  color: #374151;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 7px 16px;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;
  border: 1px solid transparent;
}

.nav-link:hover {
  color: #0840e6;
  background: rgba(8, 64, 230, 0.06);
}

.nav-link-active {
  color: #0840e6 !important;
  background: rgba(8, 64, 230, 0.08);
  border-color: rgba(8, 64, 230, 0.20);
  font-weight: 600;
}

.nav-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.nav-chevron {
  width: 12px;
  height: 12px;
  color: inherit;
  opacity: 0.6;
}

.dropdown-icon {
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  color: #6b7280;
}

.user-area {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 5px 12px 5px 6px;
  border-radius: 8px;
  border: 1px solid var(--border-default);
  background: var(--primary-subtle);
  transition: border-color 0.2s, background 0.2s;
  outline: none;
}

.user-info:hover {
  border-color: var(--border-hover);
  background: var(--primary-bg);
}

.user-avatar {
  background: var(--gradient-primary);
  color: var(--text-white);
  font-weight: 700;
  font-size: 13px;
}

.username {
  color: #374151;
  font-size: 14px;
  font-weight: 500;
}

.chevron-icon {
  width: 14px;
  height: 14px;
  color: var(--text-muted);
}

.app-content {
  flex: 1;
  min-width: 0;
  height: 100vh;
  overflow-y: auto;
  background-color: var(--bg-base);
}

.mobile-nav-backdrop,
.mobile-nav-close {
  display: none;
}

/* 成果社区手机端主导航抽屉（原型 [data-community-mobile] >.bu-side）：默认藏在左侧，汉堡菜单打开。 */
@media (max-width: 760px) {
  .app-container.community-mobile .app-sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 1301;
    box-sizing: border-box;
    width: min(300px, calc(100% - 44px));
    transform: translateX(-110%);
    visibility: hidden;
    pointer-events: none;
    box-shadow: 8px 0 32px rgba(25, 39, 70, 0.18);
    transition: transform 0.2s ease, visibility 0.2s ease;
  }
  .app-container.community-mobile.mobile-nav-open .app-sidebar {
    transform: translateX(0);
    visibility: visible;
    pointer-events: auto;
  }
  .app-container.community-mobile :deep(.sidebar-toggle) {
    display: none;
  }
  .app-container.community-mobile .mobile-nav-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1300;
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: rgba(23, 35, 61, 0.35);
    cursor: pointer;
  }
  .app-container.community-mobile .mobile-nav-close {
    position: fixed;
    top: 10px;
    left: calc(min(300px, 100% - 44px) - 52px);
    z-index: 1302;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #17233d;
    cursor: pointer;
  }
  .app-container.community-mobile .mobile-nav-close svg {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 760px) and (prefers-reduced-motion: reduce) {
  .app-container.community-mobile .app-sidebar {
    transition: none;
  }
}

.persistent-ai-chat-host {
  height: 100%;
  min-width: 0;
}

/* display:none also collapses the child layout and clears its user-scroll
   state. Clip the inactive host instead, retaining the iframe's viewport. */
.persistent-ai-chat-host.is-hidden {
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

.app-content.login-page-content {
  padding-top: 0;
  background: transparent;
}

.app-content.analysis-create-page-content {
  background: #f7f9fd;
}

.app-content.no-sidebar {
  padding-top: 0;
  height: auto;
}

.app-content.home-page-content {
  padding-top: 0;
  padding: 0;
  background: #ffffff;
}

.app-content.onboarding-page-content {
  padding-top: 0;
  padding: 0;
  background: transparent;
}

:deep(.el-dropdown-menu) {
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

:deep(.el-dropdown-menu__item) {
  color: #374151;
}

:deep(.el-dropdown-menu__item:hover) {
  background-color: rgba(8, 64, 230, 0.08);
  color: #0840e6;
}

.switch-account-inner {
  display: flex;
  align-items: center;
  width: 100%;
}

.switch-account-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.arrow-icon {
  width: 14px;
  height: 14px;
  color: #9ca3af;
  flex-shrink: 0;
}

.identity-submenu {
  position: fixed;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  padding: 6px;
  min-width: 200px;
  z-index: 9999;
}

.identity-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  gap: 8px;
}

.identity-option:hover,
.identity-option.active {
  background: rgba(8, 64, 230, 0.08);
}

.identity-option-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.identity-option-name {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

.identity-option-tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.identity-tag {
  display: inline-flex;
  align-items: center;
  padding: 0 6px;
  height: 20px;
  font-size: 11px;
  border-radius: 4px;
  font-weight: 500;
  line-height: 1;
}

.tag-personal {
  background: #dbeafe;
  color: #0840e6;
  border: 1px solid #bfdbfe;
}

.tag-enterprise {
  background: #dbeafe;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.tag-role {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.check-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

:deep(.el-popper.is-light) {
  background-color: #ffffff;
  border-color: #e5e7eb;
}

:deep(.el-popper.is-light .el-popper__arrow::before) {
  background-color: #ffffff;
  border-color: #e5e7eb;
}
</style>

<style>
.el-dropdown-menu,
.el-select-dropdown,
.el-popper.is-light {
  background-color: #ffffff !important;
  border-color: #e5e7eb !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
}

.el-dropdown-menu__item,
.el-select-dropdown__item {
  color: #374151 !important;
  background-color: transparent !important;
}

.el-dropdown-menu__item:hover,
.el-dropdown-menu__item:focus,
.el-select-dropdown__item:hover,
.el-select-dropdown__item.hover {
  background-color: rgba(8, 64, 230, 0.08) !important;
  color: #0840e6 !important;
}

.el-select-dropdown__item.is-selected,
.el-select-dropdown__item.selected {
  color: #0840e6 !important;
  background-color: rgba(8, 64, 230, 0.06) !important;
  font-weight: 600;
}

.el-popper.is-light .el-popper__arrow::before {
  background-color: #ffffff !important;
  border-color: #e5e7eb !important;
}

.el-select__wrapper {
  background-color: #ffffff !important;
  box-shadow: 0 0 0 1px #d1d5db !important;
  color: #111827 !important;
}

.el-select__wrapper:hover {
  box-shadow: 0 0 0 1px #9ca3af !important;
}

.el-select__wrapper.is-focused {
  box-shadow: 0 0 0 1px #0840e6 !important;
}

.el-select__placeholder,
.el-select__selected-item {
  color: #111827 !important;
}

.el-select__caret {
  color: #9ca3af !important;
}

.el-input__wrapper {
  background-color: #ffffff !important;
  box-shadow: 0 0 0 1px #d1d5db !important;
}

.el-input__wrapper:hover {
  box-shadow: 0 0 0 1px #9ca3af !important;
}

.el-input__wrapper.is-focus {
  box-shadow: 0 0 0 1px #0840e6 !important;
}

.el-input__inner {
  color: #111827 !important;
  background-color: transparent !important;
}

.el-input__inner::placeholder {
  color: #9ca3af !important;
}
</style>
